import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Request } from 'types/store/normalized';
import RequestTableRow from '../RequestTableRow/RequestTableRow';
import RequestTableLastRow from '../RequestTableLastRow';
import NoRequestsTable from 'Molecules/NoRequestsTable/NoRequestsTable';
import RequestsSearchEmptyState from 'Molecules/RequestsSerachEmptyState/RequestsSearchEmptyState';
import RequestsDefaultEmptyState from 'Molecules/RequestsDefaultEmptyState/RequestsDefaultEmptyState';
import LoadingError from 'Molecules/LoadingError/LoadingError';
import { requestsSelectors } from 'state/normalized/requests/requestsSlice';
import { SLICE_STATUS, SERVER_ERROR_CODES } from 'utils/constants';

interface RequestsTableBodyProps {
  requests: Request[];
  canFetchMore: boolean;
  fetchMore: () => void;
  isSearchApplied: boolean;
  areFiltersApplied: boolean;
  canSelectRows?: boolean;
  onSelectRow: (rowId: string) => void;
  selectedRowsIds: string[];
}

const RequestsTableBody: FC<RequestsTableBodyProps> = ({
  requests,
  canFetchMore,
  fetchMore,
  isSearchApplied = false,
  areFiltersApplied = false,
  canSelectRows = false,
  onSelectRow,
  selectedRowsIds,
}) => {
  const { consolidatedRequestsPage = false } = useFlags();
  const isListEmpty = requests.length === 0;
  const requestsStatus = useSelector(requestsSelectors.selectStatus);

  // A Set, not `selectedRowsIds.includes(...)` per row: the list is unbounded
  // (every loaded page stays mounted), so the linear scan inside the row map
  // was O(rows x selected).
  const selectedRowsIdsSet = useMemo(
    () => new Set(selectedRowsIds),
    [selectedRowsIds]
  );

  return (
    <tbody>
      {requestsStatus === SLICE_STATUS.FAILED && (
        <tr>
          <td colSpan={13}>
            <LoadingError
              headerHeight='17rem'
              errorCode={SERVER_ERROR_CODES.INTERNAL_SERVER_ERROR}
            />
          </td>
        </tr>
      )}
      {requestsStatus === SLICE_STATUS.SUCCESS && (
        <>
          {requests.map((request: Request, index: number) => {
            const isOdd = index % 2 !== 0;
            return (
              <RequestTableRow
                key={request.id}
                request={request}
                isOdd={isOdd}
                canSelectRows={canSelectRows}
                onSelectRow={onSelectRow}
                selected={selectedRowsIdsSet.has(request.id)}
              />
            );
          })}
          {isListEmpty &&
            !isSearchApplied &&
            !areFiltersApplied &&
            (consolidatedRequestsPage ? (
              <RequestsDefaultEmptyState />
            ) : (
              <NoRequestsTable />
            ))}
          {(isSearchApplied || areFiltersApplied) && isListEmpty && (
            <RequestsSearchEmptyState />
          )}
          {!isListEmpty && canFetchMore && (
            <RequestTableLastRow
              isOdd={requests.length % 2 !== 0}
              tableName={''}
              onLastRowInView={fetchMore}
            />
          )}
        </>
      )}
    </tbody>
  );
};

export default RequestsTableBody;
